import {
  ComponentRef,
  createComponent,
  EnvironmentInjector,
  inject,
  Injectable,
  Injector,
  OnDestroy,
  ViewContainerRef,
} from '@angular/core';
import { ComponentType } from '@angular/cdk/portal';
import {
  describeStartFailure,
  IModuleWidget,
  LazyComponentLoader,
  loadPluginChunk,
  ModuleWidgetRegistry,
  PluginChunkLoadOptions,
  PluginChunkReloadingError,
  PluginInjectorWidget,
} from 'core-client/api';
import { Observable, Subscription } from 'rxjs';

export interface IWidgetViewInstance {
  isHeaderHovered?: boolean;
  data?: PluginInjectorWidget.IWidget;
  extraTitle?: Observable<string>;
}

export interface WidgetHost {
  readonly widgetId: string;
  readonly componentRef: ComponentRef<IWidgetViewInstance>;
}

interface WidgetHostRecord extends WidgetHost {
  attachedTo?: ViewContainerRef;
  playingVideos: HTMLVideoElement[];
  extraTitleSubscription?: Subscription;
}

@Injectable()
export class WidgetHostRegistry implements OnDestroy {
  private readonly injector = inject(Injector);
  private readonly environmentInjector = inject(EnvironmentInjector);
  private readonly moduleWidgetRegistry = inject(ModuleWidgetRegistry);
  private readonly hosts = new Map<string, WidgetHostRecord>();
  private readonly pending = new Map<string, Promise<WidgetHost | undefined>>();
  private destroyed = false;
  protected chunkLoadOptions: PluginChunkLoadOptions = {};

  acquire(
    widget: PluginInjectorWidget.IWidget,
    isHeaderHovered: boolean,
  ): Promise<WidgetHost | undefined> {
    const existing = this.hosts.get(widget._id);
    if (existing) {
      existing.componentRef.instance.isHeaderHovered = isHeaderHovered;
      return Promise.resolve(existing);
    }
    const inFlight = this.pending.get(widget._id);
    if (inFlight) {
      return inFlight;
    }
    const creation = this.create(widget, isHeaderHovered).finally(() =>
      this.pending.delete(widget._id),
    );
    this.pending.set(widget._id, creation);
    return creation;
  }

  mount(host: WidgetHost, viewContainerRef: ViewContainerRef): void {
    const record = this.hosts.get(host.widgetId);
    if (!record) {
      return;
    }
    viewContainerRef.insert(record.componentRef.hostView);
    record.attachedTo = viewContainerRef;
    this.resumeVideos(record);
  }

  detachAll(): void {
    let detached = 0;
    for (const record of this.hosts.values()) {
      const container = record.attachedTo;
      if (!container) {
        continue;
      }
      // Browsers pause media removed from the document.
      record.playingVideos = this.videosOf(record).filter(
        (video: HTMLVideoElement) => !video.paused && !video.ended,
      );
      const index = container.indexOf(record.componentRef.hostView);
      if (index >= 0) {
        container.detach(index);
      }
      record.attachedTo = undefined;
      detached++;
    }
    if (detached > 0) {
      console.debug(
        `[WidgetHostRegistry] detached ${detached} widget host(s) for the layout switch`,
      );
    }
  }

  setHeaderHovered(widgetId: string, hovered: boolean): void {
    const record = this.hosts.get(widgetId);
    if (record) {
      record.componentRef.instance.isHeaderHovered = hovered;
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    for (const record of [...this.hosts.values()]) {
      record.componentRef.destroy();
    }
    this.hosts.clear();
  }

  private async create(
    widget: PluginInjectorWidget.IWidget,
    isHeaderHovered: boolean,
  ): Promise<WidgetHost | undefined> {
    const moduleWidget = this.moduleWidgetRegistry
      .getModuleWidgets()
      .find(
        (candidate: IModuleWidget) =>
          candidate.pluginId === widget.pluginId &&
          candidate.pluginModule === widget.pluginModule,
      );
    if (!moduleWidget?.viewComponent) {
      return undefined;
    }
    let component: ComponentType<IWidgetViewInstance>;
    try {
      component = await loadPluginChunk(
        `${widget.pluginModule}-widget`,
        () => resolveComponent(moduleWidget.viewComponent.component),
        { ...this.chunkLoadOptions, label: `[Widgets] "${widget.name}"` },
      );
    } catch (error) {
      const failure = error as Error | string;
      if (!(failure instanceof PluginChunkReloadingError)) {
        console.error(
          `[Widgets] "${widget.name}" could not be loaded: ${describeStartFailure(failure)}. Reload to retry.`,
        );
      }
      return undefined;
    }
    if (this.destroyed) {
      return undefined;
    }

    const componentRef = createComponent<IWidgetViewInstance>(component, {
      environmentInjector: this.environmentInjector,
      elementInjector: this.injector,
    });
    componentRef.instance.isHeaderHovered = isHeaderHovered;
    componentRef.instance.data = {
      appPluginWidget: widget.appPluginWidget,
      pluginId: widget.pluginId,
      pluginModule: widget.pluginModule,
      widgetPermissions: moduleWidget.widgetPermissions,
      publicView: widget.publicView,
    } as PluginInjectorWidget.IWidget;

    const record: WidgetHostRecord = {
      widgetId: widget._id,
      componentRef,
      playingVideos: [],
    };
    record.extraTitleSubscription = componentRef.instance.extraTitle?.subscribe(
      (extraTitle: string) => {
        widget.extraTitle = extraTitle.trim().length > 0 ? `: ${extraTitle}` : '';
      },
    );
    componentRef.onDestroy(() => {
      record.extraTitleSubscription?.unsubscribe();
      if (this.hosts.get(widget._id) === record) {
        this.hosts.delete(widget._id);
      }
    });
    this.hosts.set(widget._id, record);
    return record;
  }

  private resumeVideos(record: WidgetHostRecord): void {
    const videos = record.playingVideos;
    record.playingVideos = [];
    if (videos.length === 0) {
      return;
    }
    for (const video of videos) {
      video.play().catch(() => undefined);
    }
    console.debug(
      `[WidgetHostRegistry] resumed ${videos.length} video(s) in widget ${record.widgetId} after the layout switch`,
    );
  }

  private videosOf(record: WidgetHostRecord): HTMLVideoElement[] {
    const element = record.componentRef.location.nativeElement as HTMLElement;
    return Array.from(element.querySelectorAll('video'));
  }
}

function resolveComponent(
  componentOrLoader: ComponentType<IWidgetViewInstance> | LazyComponentLoader,
): Promise<ComponentType<IWidgetViewInstance>> {
  if ((componentOrLoader as { prototype?: object }).prototype !== undefined) {
    return Promise.resolve(componentOrLoader as ComponentType<IWidgetViewInstance>);
  }
  return (componentOrLoader as LazyComponentLoader)();
}
